import pluralize from 'pluralize';
import dayjs from '~/timeInit';

import { Brand } from '@luxuryescapes/lib-regions/lib/regions';

import { QantasPartnership } from '~/consts/partnerships';

import {
  type AccrualLogResponse,
  type CalculateEarnRequest,
  type CalculateEarnResponse,
  type CreateCustomRuleRequestBody,
  type CreateCustomRuleResponse,
  type CreateLoyaltyAccountRequest,
  type CreateLoyaltyAccountResponse,
  type CustomRuleSearchParams,
  type CustomRuleSearchResponse,
  type GetLoyaltyAccountResponse,
  type ManualAccrualFilterParams,
  type ManualAccrualsResponse,
  type ManualAccrualsRetryRequestBody,
  type ManualAccrualsRetryResponse,
  type PointsAdjustmentRequestBody,
  type PointsAdjustmentResponse,
  type UpdateCustomRuleRequestBody,
  type UpdateCustomRuleResponse,
  type UpdateManualAccrualStatusRequest,
  type UpdateManualAccrualStatusResponse,
} from '~/types/loyaltyContract';

import { request } from './common';

const basePath = () => window.configs.API_HOST + '/api/';

const isUpperCase = (aCharacter) => aCharacter >= 'A' && aCharacter <= 'Z';

export const accountFieldsMap = (partnership) => {
  const data = [];

  for (const field of partnership.accountFields) {
    if (field === 'account_id') {
      data.push({
        name: 'Account',
        field: `${partnership.prefix}_number`,
        accountField: field,
      });
    } else if (field === 'last_name') {
      data.push({
        name: 'Surname',
        field: `${partnership.prefix}_surname`,
        accountField: field,
      });
    } else if (field === 'first_name') {
      data.push({
        name: 'GivenName',
        field: `${partnership.prefix}_given_name`,
        accountField: field,
      });
    }
  }

  return data;
};

export const getBonusUnitPluralize = (partnership, number = 0) => {
  return pluralize(partnership.bonusUnit, number);
};

export const convertCodeToSlug = (code) => {
  const word = [];

  for (let count = 0; count < code.length; count++) {
    const letter = code.charAt(count);
    if (isUpperCase(letter)) {
      word.push(`-${letter.toLowerCase()}`);
    } else {
      word.push(letter);
    }
  }

  return word.join('');
};

export const getPartnerships = async (
  region = '',
  brand: Brand = 'luxuryescapes',
): Promise<App.PartnershipsApiResponse> => {
  const response = await request(`${basePath()}loyalty/partnerships?region=${region}&brand=${brand}`, {
    method: 'GET',
  });

  // We are adding Qantas to the list of partnerships for the time being in order to view reports.
  response.result = [...response.result, QantasPartnership];
  response.count = response.result.length;

  return response;
};

export const getPartnershipByCode = async (code) => {
  return request(`${basePath()}loyalty/partnerships/${code}`, {
    method: 'GET',
  });
};

export const getLoyaltyBurn = async (id_member, code, brand) => {
  return request(`${basePath()}loyalty/${convertCodeToSlug(code)}/burn?member_id=${id_member}&brand=${brand}`, {
    method: 'GET',
  });
};

export const getLoyaltyEarn = async (id_member, code, brand) => {
  return request(`${basePath()}loyalty/${convertCodeToSlug(code)}?member_id=${id_member}&brand=${brand}`, {
    method: 'GET',
  });
};

export const getEarnOrder = (order, allPartnerships) => {
  const data = [];
  for (const partnership of allPartnerships) {
    const orderPartnership = order.partnerships?.[partnership.prefix];
    if (orderPartnership) {
      data.push(
        new Promise((resolve, reject) => {
          request(
            `${basePath()}loyalty/${convertCodeToSlug(partnership.code)}?member_id=${order.fk_customer_id}&order_id=${
              order.id_orders
            }&brand=${order.brand}`,
            { method: 'GET' },
          )
            .then((result) => {
              resolve({
                prefix: partnership.prefix,
                code: partnership.code,
                result: result.result,
              });
            })
            .catch(reject);
        }),
      );
    }
  }

  return data;
};

export function earnData(data, partnership) {
  const accountFields = accountFieldsMap(partnership);
  let postData = {};

  if (partnership.prefix === 'qff') {
    postData = {
      points_base_multiplier: data.points_base_multiplier,
      has_bonus_multiplier: data.has_bonus_multiplier,
      points_bonus_multiplier: data.points_bonus_multiplier,
    };
  } else if (partnership.prefix === 'kfp') {
    postData = {
      miles_base_earned: data.base_points,
      miles_bonus_earned: data.bonus_points,
      miles_total_earned: data.base_points + data.bonus_points,
    };
  }

  accountFields.forEach((fieldData) => {
    postData[fieldData.field] = data[fieldData.accountField] || '';
  });

  return {
    member_id: data.member_id,
    member_given_name: data.member_given_name,
    member_surname: data.member_surname,
    item_id: data.item_id,
    order_id: data.order_id,
    order_item_name: data.order_item_name,
    order_date: data.order_date,
    order_payment_amount: parseInt(data.points_requested),
    currency_code: data.currency_code,
    request_by: data.request_by,
    request_reason: data.request_reason,
    request_comment: data.request_comment,
    ...postData,
  };
}

export async function postEarnData(data, code) {
  return request(`${basePath()}loyalty/${convertCodeToSlug(code)}`, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: { 'Content-Type': 'application/json' },
  });
}

export function determineLoyaltyStatus(order, allPartnerships) {
  const orderDate = dayjs(order.created_at).tz('Australia/Sydney').format('YYYY-MM-DD');
  const launchDate = dayjs('01/02/2018', 'DD/MM/YYYY');
  const isBeforeLaunch = dayjs(orderDate).isBefore(launchDate);
  const isCompletedPending = order.status === 'completed' || order.status === 'pending';

  const eligibleDate = dayjs().tz('Australia/Sydney').subtract(30, 'days').format('YYYY-MM-DD');
  const isPostThirtyDays = dayjs(orderDate).isBefore(eligibleDate);
  const isEligible = !isBeforeLaunch && isCompletedPending;

  const data = {};
  for (const partnership of allPartnerships) {
    const orderPartnership = order.partnerships?.[partnership.prefix];
    if (orderPartnership) {
      data[partnership.prefix] = {
        prefix: partnership.prefix,
        code: partnership.code,
        isEligible: isEligible,
        isPostThirtyDays: isPostThirtyDays,
        isCompletedPending: isCompletedPending,
        message: '',
      };

      if (isBeforeLaunch || !isCompletedPending) {
        data[partnership.prefix].message = `This order does not qualify for ${partnership.rewardName}`;
      } else if (!isPostThirtyDays) {
        data[partnership.prefix].message = `This order is still within the 30-day waiting period`;
      }
    }
  }

  return data;
}

interface RequestOptions {
  signal?: AbortSignal;
}

export async function searchCustomRules(
  params: CustomRuleSearchParams,
  options: RequestOptions = {},
): Promise<CustomRuleSearchResponse> {
  return request(`${basePath()}lux-loyalty/custom-rules/search`, {
    ...options,
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function createCustomRule(params: CreateCustomRuleRequestBody): Promise<CreateCustomRuleResponse> {
  return request(`${basePath()}lux-loyalty/custom-rules`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function updateCustomRule(
  customRuleId: string,
  params: UpdateCustomRuleRequestBody,
): Promise<UpdateCustomRuleResponse> {
  return request(`${basePath()}lux-loyalty/custom-rules/${customRuleId}`, {
    method: 'PATCH',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function getManualAccruals(
  params: ManualAccrualFilterParams,
  options: RequestOptions = {},
): Promise<ManualAccrualsResponse> {
  const queryParams = new URLSearchParams();
  if (params.page) {
    queryParams.append('page', params.page.toString());
  }
  if (params.pageSize) {
    queryParams.append('pageSize', params.pageSize.toString());
  }
  if (params.status) {
    queryParams.append('status', params.status);
  }
  if (params.customerId) {
    queryParams.append('customerId', params.customerId);
  }
  if (params.orderId) {
    queryParams.append('orderId', params.orderId);
  }
  if (params.itemId) {
    queryParams.append('itemId', params.itemId);
  }
  if (params.productType) {
    queryParams.append('productType', params.productType);
  }

  const queryString = queryParams.toString();
  const url = `${basePath()}lux-loyalty/manual-accruals${queryString ? `?${queryString}` : ''}`;

  return request(url, {
    ...options,
    method: 'GET',
  });
}

export async function retryManualAccruals(
  params: ManualAccrualsRetryRequestBody,
): Promise<ManualAccrualsRetryResponse> {
  return request(`${basePath()}lux-loyalty/manual-accruals`, {
    method: 'PUT',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function calculateEarnPoints(params: CalculateEarnRequest): Promise<CalculateEarnResponse> {
  return request(`${basePath()}lux-loyalty/points/calculate-earn`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function getLoyaltyAccount(customerId: string): Promise<GetLoyaltyAccountResponse> {
  return request(`${basePath()}lux-loyalty/account?customerId=${customerId}`, {
    method: 'GET',
  });
}

export async function createLoyaltyAccount(params: CreateLoyaltyAccountRequest): Promise<CreateLoyaltyAccountResponse> {
  return request(`${basePath()}lux-loyalty/account`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

// Address Autocomplete Types
export interface AddressDisplay {
  addressId: string;
  mainText: string;
  secondaryText: string;
  formatted_address: string;
}

export interface AddressComponents {
  unit_number?: string;
  street?: string;
  city?: string;
  state?: string;
  postcode?: string;
  country?: string;
}

export interface AddressSuggestion {
  latitude?: number;
  longitude?: number;
  display: AddressDisplay;
  address_components: AddressComponents;
}

export interface AddressAutocompleteResponse {
  status: number;
  message: string;
  result: Array<AddressSuggestion>;
}

export function getAddressSuggestions(input: string, options?: RequestOptions): Promise<AddressAutocompleteResponse> {
  const url = `${basePath()}lux-loyalty/address?input=${encodeURIComponent(input)}`;

  return request(url, { method: 'GET', ...options });
}

/**
 * Fetches the combined accrual log for a specific order item.
 * Returns IBS accrual logs, manual accrual record (if any), and points adjustment records.
 */
export function getAccrualLog(
  orderId: string,
  itemId: string,
  options: RequestOptions = {},
): Promise<AccrualLogResponse> {
  const url = `${basePath()}lux-loyalty/manual-accruals/accrual-log?orderId=${encodeURIComponent(
    orderId,
  )}&itemId=${encodeURIComponent(itemId)}`;

  return request(url, { method: 'GET', ...options });
}

/**
 * Creates a points adjustment (credit or debit) for a customer account.
 * Used by CS to manually allocate points when the automated accrual process has failed.
 */
export function createPointsAdjustment(params: PointsAdjustmentRequestBody): Promise<PointsAdjustmentResponse> {
  return request(`${basePath()}lux-loyalty/points/adjustments`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Updates the status of a manual accrual record, optionally recording who resolved it.
 * Used by CS to mark an item as already given when points were allocated manually via IBS.
 */
export function updateManualAccrualStatus(
  itemId: string,
  params: UpdateManualAccrualStatusRequest,
): Promise<UpdateManualAccrualStatusResponse> {
  return request(`${basePath()}lux-loyalty/manual-accruals/${encodeURIComponent(itemId)}/status`, {
    method: 'PATCH',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}
